import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "./components/Seo";
import { Container, PageHero, CtaButton, FinalCta, SectionHeader } from "./components/primitives";
import { COMPANY } from "./content/site";

const LEGAL = {
  "privacy-policy": {
    title: "Privacy Policy",
    sections: [
      ["Information we collect", "When you submit a form we collect the information you provide — such as name, company, email, phone, resume and message — so we can respond to your request."],
      ["How we use information", "We use your information to respond to inquiries, evaluate candidates, deliver services and improve the website. We do not sell personal information."],
      ["Analytics", "We collect aggregated, non-identifying interaction events (such as CTA clicks and form submissions) to understand how the site is used."],
      ["Retention", "We retain lead and application information for as long as needed to fulfil the purpose it was collected for, or as required by law."],
      ["Your choices", `To access, correct or delete your information, contact ${COMPANY.email}.`],
      ["Updates", "This policy may be updated. Material changes will be reflected on this page. Legal review is recommended before production use."],
    ],
  },
  terms: {
    title: "Terms of Service",
    sections: [
      ["Use of this website", "This website is provided for informational purposes. Content may change without notice."],
      ["No professional advice", "Content does not constitute legal, financial or professional advice. Engagement terms are defined in signed agreements."],
      ["Intellectual property", `Site content and branding are the property of ${COMPANY.legal} unless otherwise noted.`],
      ["Limitation of liability", "To the extent permitted by law, we are not liable for indirect or consequential damages arising from use of this site."],
      ["Contact", `Questions about these terms: ${COMPANY.email}. Legal review is recommended before production use.`],
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    sections: [
      ["Our commitment", "We aim to make this website usable by everyone, including people who rely on assistive technologies."],
      ["Measures", "Keyboard-navigable menus, cards, modals and forms; visible focus states; semantic landmarks; labelled form controls; reduced-motion support; and a non-WebGL fallback for the hero visual."],
      ["Feedback", `If you encounter a barrier, email ${COMPANY.email} and we will work to address it.`],
    ],
  },
};

export function LegalPage({ slug }) {
  const page = LEGAL[slug];
  return (
    <>
      <Seo title={page.title} description={`${page.title} for ${COMPANY.name}.`} />
      <PageHero eyebrow="Legal" title={page.title} compact />
      <section className="py-20 bg-light"><Container className="max-w-3xl">
        {page.sections.map(([h, p]) => <div key={h} className="mb-10"><h2 className="font-display text-xl font-bold">{h}</h2><p className="mt-3 text-muted-foreground leading-relaxed">{p}</p></div>)}
        <p className="text-xs text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long" })}</p>
      </Container></section>
    </>
  );
}

const FAQS = [
  { q: "What does TechAI Fortune do?", a: "We are a U.S. technology consulting and workforce solutions company. We deliver AI, cloud, data, automation and software programs and provide staffing across office administration, healthcare, manufacturing, technology, financial and professional services." },
  { q: "How do engagements start?", a: "Book a consultation. We run a Discover session to understand objectives, systems and constraints, then propose a scope with success criteria." },
  { q: "Do you offer managed services?", a: "Yes. Every service area is available as Advisory, Implementation, Migration or Managed Services." },
  { q: "What staffing models do you support?", a: "Temporary, contract, contract-to-hire, direct hire, executive search and managed workforce programs." },
  { q: "How fast can you provide candidates?", a: "It depends on role, location and market. We confirm a realistic timeline during intake rather than promising a fixed number." },
  { q: "Where are you located?", a: `${COMPANY.legal} is headquartered in Houston, Texas and serves clients across the United States.` },
];

export function FaqPage() {
  return (
    <>
      <Seo title="FAQ" description="Frequently asked questions about TechAI Fortune services, engagements and staffing." schema={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      <PageHero eyebrow="FAQ" title="Frequently asked questions." compact />
      <section className="py-20 bg-light"><Container className="max-w-3xl">
        <Accordion type="single" collapsible>{FAQS.map((f, i) => <AccordionItem key={i} value={`q${i}`} data-testid={`faq-${i}`}><AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger><AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent></AccordionItem>)}</Accordion>
      </Container></section>
      <FinalCta title="Still have a question?" primary="Start a Conversation" secondary="Request Staffing" />
    </>
  );
}

export function ComingSoon({ title, desc }) {
  return (
    <>
      <Seo title={title} description={desc} />
      <PageHero eyebrow="Resources" title={title} desc={desc} compact><CtaButton to="/case-studies" variant="light">Browse case studies <ArrowRight className="ml-2 h-4 w-4" /></CtaButton></PageHero>
      <section className="py-24 bg-light"><Container><SectionHeader eyebrow="Coming soon" title="Content is being prepared." desc="Only approved and verified material is published here. Check back soon or contact us directly." /></Container></section>
    </>
  );
}

export function NotFound() {
  return (
    <>
      <Seo title="Page not found" description="The page you requested could not be found." />
      <section className="relative overflow-hidden bg-navy text-white min-h-[70vh] grid place-items-center" data-testid="not-found-page">
        <div className="absolute inset-0 bg-grid-dark opacity-30" /><div className="absolute inset-x-0 bottom-0 h-1/2 perspective-grid" />
        <Container className="relative text-center py-24">
          <div className="eyebrow text-[#00D4FF]">Error 404</div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold">This page moved or never existed.</h1>
          <p className="mt-5 text-white/65 max-w-md mx-auto">Use the navigation above, or head back home.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3"><CtaButton to="/" testId="notfound-home">Back to Home</CtaButton><CtaButton to="/contact" variant="outline">Contact us</CtaButton></div>
          <div className="mt-10 flex flex-wrap justify-center gap-4 text-sm text-white/50">{[["/services", "Services"], ["/solutions", "Solutions"], ["/industries", "Industries"], ["/careers", "Careers"]].map(([to, l]) => <Link key={to} to={to} className="hover:text-white">{l}</Link>)}</div>
        </Container>
      </section>
    </>
  );
}
